import { useState } from 'react'
import { Temporal } from '@imago/api-client'
import { CancelButton, cancelDialog, Dialog, InteractiveMutation, MutateButton, SubmitButton, useDeclareIntent } from '@imago/api-client-for-react'
import { CURRENT_CARE_MONTHLY_PRICE_EURCENTS, InvoiceInfo } from '@imago/model'
import { useStripeFinalizationState, _useTerminateCare, useCare, FatCareInfo, Zoned, useNow, _useSubscribe } from '../api.ts'
import { PaymentDialog, usePayInvoice } from './PaymentDialog.tsx'

const locale = 'en-GB'

export function ImagoCare({preview}: {preview?: boolean}) {
	const care = useCare()
	if (care === undefined) return
	return <_ImagoCare care={care} preview={preview}/>
}

export function formatDays(duration: Temporal.Duration) {
	const days = duration.round('days').total('days')
	return (
		days === -1 ? 'yesterday' :
		days < 0 ? -days + ' days ago' :
		days === 1 ? 'tommorow' :
		days > 0 ? 'in ' + days + ' days' :
		'today'
	)
}

function _ImagoCare({preview, care}: {care: Zoned<FatCareInfo>, preview?: boolean}) {
	const now = useNow('minute')
	const setUpSubscription = useSubscribe()
	const terminateCare = useTerminateCare()

	const setUp = care.nextCharge !== null
	const paid = care.unpaidInvoices.length === 0
	const subscriptionPaid = care.unpaidInvoices.filter(i => i.nextAttemptOn).length === 0
	const status =
		care.nextCharge ? 'set up' :
		care.terminated ? (Temporal.ZonedDateTime.compare(care.terminated, now) > 0 ? `terminates` : `terminated`) + ` at ${care.terminated.toLocaleString(locale, {day: 'numeric', month: 'short'})}` :
		care.started ? 'missing payment method' :
		''

	const [detailsShown, setDetailsShown] = useState<boolean>(!setUp || !paid)

	return <article>
			<hgroup aria-controls="care-details" onClick={ev => {
				if (ev.defaultPrevented) return
				ev.preventDefault()
				setDetailsShown(!detailsShown)
			}}>
				<h5>Imago Care</h5>
				<dd hidden={detailsShown || (setUpSubscription ? setUpSubscription.isPending : false) || (terminateCare ? terminateCare.isPending : false)} style={{textAlign: 'right'}}>
					<strong style={{
						color: setUp ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-error)',
					}}>{status}</strong>
					{!paid ? <>
						,<br/><strong style={{color: 'var(--md-sys-color-error)'}}>unpaid</strong>
					</> : <></>}
				</dd>
				<div role="toolbar">
					<button>
						<span className="material-symbols-outlined">{!detailsShown ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}</span>
					</button>
				</div>
			</hgroup>
		<div id="care-details" hidden={!detailsShown}>
			<p>Imago Care covers supervision and guidance over your transition process, GAHT prescription renewals and adjustments, and support by our team.</p>
			{care.started ? <>
				<dl>
					<dt>Started</dt>
					<dd>{care.started.toLocaleString(locale, {dateStyle: 'full'})} <span style={{whiteSpace: 'nowrap'}}>({formatDays(now.until(care.started))})</span></dd>
				</dl>
			</> : <></>}
			{status ? <>
				<dl>
					<dt>Status</dt>
					<dd>
						<strong style={{
							color: setUp ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-error)'
						}}>{status}</strong>
						{!paid ? <>
							, <strong style={{color: 'var(--md-sys-color-error)'}}>unpaid</strong>
						</> : <></>}
					</dd>
				</dl>
			</> : <></>}
			{care.nextCharge ? <>
				<dl>
					<dt>Next charge</dt>
					<dd>{care.nextCharge.toLocaleString(locale, {dateStyle: 'full'})} <span style={{whiteSpace: 'nowrap'}}>({formatDays(now.until(care.nextCharge))})</span></dd>
				</dl>
			</> : <></>}
			<dl>
				<dt>Price</dt>
				<dd>{(CURRENT_CARE_MONTHLY_PRICE_EURCENTS / 100).toLocaleString(locale, {style: 'currency', currency: 'EUR'})}/month</dd>
			</dl>
			{!preview ? <>
				<MutateButton className={!setUp || !subscriptionPaid ? 'primary' : undefined} mutation={setUpSubscription}>{!setUp ? 'Set up subscription' : 'Change payment method'}</MutateButton>
				<MutateButton className="danger" mutation={terminateCare}>Terminate care</MutateButton>
				{care.unpaidInvoices.length ? <>
					<hr/>
					{care.unpaidInvoices.map(invoice => <UnpaidInvoice key={invoice.ID} invoice={invoice}/>)}
				</> : <></>}
			</> : <></>}
		</div>
		<PaymentDialog mutation={setUpSubscription}/>
		<TerminateCareDialog mutation={terminateCare}/>
	</article>
}

function UnpaidInvoice({invoice}: {invoice: Zoned<InvoiceInfo>}) {
	const pay = usePayInvoice({invoiceID: invoice.ID})

	const showStartYear = invoice.periodStart.year !== invoice.periodEnd.year

	return <div className="row" key={invoice.number}>
		<div style={{flex: '1', gap: '0', justifyContent: 'center'}}>
			<span className="text">
				<strong>Unpaid {invoice.amountRemaining.toLocaleString(locale, {style: 'currency', currency: 'EUR'})}</strong><br/>
				<small>Due by: <span style={{whiteSpace: 'nowrap'}}>{invoice.dueBy.toLocaleString(locale, {dateStyle: 'long'})}</span></small>
			</span>
		</div>
		<div style={{flex: '1', gap: '0', justifyContent: 'center'}}>
			<span className="text">
				<small>{invoice.periodStart.toLocaleString(locale, {month: 'short', day: 'numeric', year: showStartYear ? 'numeric' : undefined})} - {invoice.periodEnd.toLocaleString(locale, {dateStyle: 'medium'})}</small>
			</span>
		</div>
		<div style={{flex: '1', gap: '0', justifyContent: 'center'}}>
			<MutateButton className="high-prio" mutation={pay}>Pay</MutateButton>
			<span className="text" style={{textAlign: 'center'}}>
				{invoice.nextAttemptOn
					? <small>auto-retries on <span style={{whiteSpace: 'nowrap'}}>{invoice.nextAttemptOn.toLocaleString(locale, {dateStyle: 'long'})}</span></small>
					: <small></small>}
			</span>
		</div>
		<PaymentDialog mutation={pay}/>
	</div>
}

function useSubscribe() {
	const _subscribe = _useSubscribe()
	const subscribe = useDeclareIntent(async () => {
		const {intent} = await _subscribe.mutateAsync()
		return intent
	})

	return {
		...subscribe,
		request: {verb: 'subscribe'} as const,
	}
}

function useTerminateCare() {
	const finalization = useStripeFinalizationState()
	const care = useCare()
	const dialog = useDeclareIntent(async () => ({}))

	if (care === undefined) return
	return care.nextCharge || (care.started && !care.terminated) ? {
		...dialog,
		isPending: finalization.isPending || dialog.isPending,
	} : null
}

function TerminateCareDialog({mutation}: {
	mutation: InteractiveMutation | undefined | null
}) {
	const terminateCare = _useTerminateCare()

	return !mutation ? <></> : <Dialog mutation={mutation}>
	<form>
		<h4 className="danger">Terminate Imago Care?</h4>
		<div>
			<p>Are you sure you want to cancel Imago Care?</p>
			<p>Imago Care covers supervision and guidance over your transition process, GAHT prescription renewals and adjustments, and support by our team.</p>
			<p className="danger">To restart Imago Care in the future - you'll need to pay for the time it was cancelled.</p>
		</div>
		<div className="row">
			<SubmitButton className="danger" mutation={terminateCare} vars={data => {}} onSuccess={cancelDialog}>Cancel subscription</SubmitButton>
			<CancelButton>Remain subscribed</CancelButton>
		</div>
	</form>
	</Dialog>
}
